/** Backend enum for POST/PATCH body `type` field */
export type KoreanSchoolContentBodyType = 'STATISTICS' | 'SAMPLE' | 'SUPPORT' | 'ACTIVITY'

/** Query param `type` for GET /api/org/korean-school-contents */
export enum KoreanSchoolContentQueryType {
  STATISTICS = 1,
  SAMPLE = 2,
  SUPPORT = 3,
  ACTIVITY = 4,
}

export interface IKoreanSchoolContent {
  id: number
  type: KoreanSchoolContentBodyType | string
  titleUz: string
  titleRu: string
  titleKr: string
  contentUz: string
  contentRu: string
  contentKr: string
  createdDate?: string
  lastModifiedDate?: string
}

export type KoreanSchoolContentPayload = {
  type: KoreanSchoolContentBodyType
  titleUz: string
  titleRu: string
  titleKr: string
  contentUz: string
  contentRu: string
  contentKr: string
}

/** Org/account JSON: `{ success, message, data: T | T[] }` */
export type KoreanSchoolApiEnvelope<T = unknown> = {
  success: boolean
  message?: string
  data: T
}

function isApiEnvelope(raw: unknown): raw is KoreanSchoolApiEnvelope {
  return (
    !!raw &&
    typeof raw === 'object' &&
    'success' in raw &&
    typeof (raw as KoreanSchoolApiEnvelope).success === 'boolean' &&
    'data' in raw
  )
}

/** Unwrap `{ success, message, data }` to inner `data`, or return `raw`. */
export function unwrapKoreanSchoolApiData(raw: unknown): unknown {
  if (isApiEnvelope(raw)) {
    return raw.data
  }
  return raw
}

/** Single content from org detail when body is envelope or plain entity. */
export function normalizeKoreanSchoolContentDetail(raw: unknown): IKoreanSchoolContent | undefined {
  const inner = unwrapKoreanSchoolApiData(raw)
  if (inner && typeof inner === 'object' && !Array.isArray(inner) && 'id' in inner) {
    return inner as IKoreanSchoolContent
  }
  return undefined
}

/** Org list API: envelope `{ data: [] }`, bare array, or page object (`content`, `items`). */
export function normalizeKoreanSchoolContentsList(raw: unknown): IKoreanSchoolContent[] {
  const unwrapped = unwrapKoreanSchoolApiData(raw)
  if (Array.isArray(unwrapped)) {
    return unwrapped
  }
  if (unwrapped && typeof unwrapped === 'object') {
    const o = unwrapped as Record<string, unknown>
    if (Array.isArray(o.content)) {
      return o.content as IKoreanSchoolContent[]
    }
    if (Array.isArray(o.data)) {
      return o.data as IKoreanSchoolContent[]
    }
    if (Array.isArray(o.items)) {
      return o.items as IKoreanSchoolContent[]
    }
  }
  return []
}

/** Total count from `x-total-count`, page/envelope totals, or list length. */
export function getKoreanSchoolContentsTotalCount(
  raw: unknown,
  headerTotal: string | number | undefined,
  itemsLength: number,
): number {
  const fromHeader = Number(headerTotal)
  if (!Number.isNaN(fromHeader) && fromHeader > 0) {
    return fromHeader
  }
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const root = raw as { totalElements?: number; total?: number }
    if (typeof root.totalElements === 'number' && root.totalElements >= 0) {
      return root.totalElements
    }
    if (typeof root.total === 'number' && root.total >= 0) {
      return root.total
    }
  }
  return itemsLength
}
